/**
 * Role Utilities — Frontend
 *
 * Re-exports canonical role data from featureFlagDefinitions and adds
 * invite-system helpers needed by Phase 1+.
 */

import { ROLE_HIERARCHY, ROLE_LABELS, meetsMinRole } from "../config/featureFlagDefinitions";

export { ROLE_HIERARCHY, ROLE_LABELS, meetsMinRole };

/**
 * Get the numeric level for a role (-1 for unknown).
 * @param {string} role
 * @returns {number}
 */
export function getRoleLevel(role) {
  return ROLE_HIERARCHY.indexOf(role);
}

/** Default invite caps per role (how many users each role can invite). */
export const DEFAULT_INVITE_CAPS = {
  viewer: 0,
  clinician: 0,
  standard: 5,
  manager: 10,
  companyAdmin: 20,
  superAdmin: Infinity,
};

/**
 * Return the effective invite cap for a user.
 * Uses the user's explicit `inviteCap` if set, otherwise falls back to role default.
 * @param {Object} user - User document (must have `userRole`, may have `inviteCap`)
 * @returns {number}
 */
export function getEffectiveInviteCap(user) {
  if (user?.inviteCap != null) return user.inviteCap;
  const role = user?.userRole || "standard";
  return DEFAULT_INVITE_CAPS[role] ?? 0;
}

/**
 * Return roles in the order they should appear in the invite form. Roles are
 * normally below the inviter; standard users may invite another standard user.
 * @param {string} inviterRole
 * @returns {string[]}
 */
export function getAssignableRoles(inviterRole) {
  const level = getRoleLevel(inviterRole);
  if (level < getRoleLevel("standard")) return [];
  const end = inviterRole === "standard" ? level + 1 : level;
  return ROLE_HIERARCHY.slice(0, end).reverse();
}
